import { olarkId } from '@fv/client-core'

import { emitOlarkEvent } from './olarkEvents.js'
import type { OlarkFn } from './useOlarkCommands.js'

const OLARK_LOADER_SCRIPT_ID = 'olark-loader-script'
const OLARK_LOADER_SRC = '//static.olark.com/jsclient/loader.js'
const OLARK_READY_TIMEOUT_MS = 10_000

export type OlarkEmbedMode = 'floating' | 'inline'

export type OlarkInternal = {
  s: IArguments[]
  t: number[]
  c: Record<string, unknown>
  l: string
  i: string | undefined
}

export type LoadOlarkOptions = {
  mode?: OlarkEmbedMode
  onReady?: () => void
}

let pendingLoad: Promise<void> | null = null
let olarkReady = false
let eventHandlersRegistered = false
let loadedMode: OlarkEmbedMode | null = null

export const isOlarkReady = (): boolean => olarkReady

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error('Timed out waiting for Olark')),
      ms,
    )
    promise.then(
      value => {
        window.clearTimeout(timeout)
        resolve(value)
      },
      error => {
        window.clearTimeout(timeout)
        reject(error)
      },
    )
  })

const createOlarkStub = (): void => {
  const olarkInternal: OlarkInternal = {
    s: [],
    t: [+new Date()],
    c: {},
    l: 'static.olark.com/jsclient/loader.js',
    i: undefined,
  }

  window.olark = function () {
    olarkInternal.s.push(arguments)
    olarkInternal.t.push(+new Date())
  } as OlarkFn

  window.olark._ = olarkInternal
}

const applyOlarkConfig = (mode: OlarkEmbedMode): void => {
  window.olark?.('configure', 'system.is_single_page_application', true)

  if (mode === 'inline') {
    window.olark?.('configure', 'box.inline', true)
    window.olark?.('configure', 'box.start_expanded', true)
    window.olark?.('configure', 'system.hb_hide_minimize_button', true)
    window.olark?.('configure', 'box.start_hidden', false)
    return
  }

  window.olark?.('configure', 'box.start_hidden', false)
}

const registerOlarkEventHandlers = (): void => {
  if (eventHandlersRegistered) return

  window.olark?.('api.chat.onBeginConversation', () => {
    emitOlarkEvent('beginConversation')
  })
  window.olark?.(
    'api.chat.onCommandFromOperator',
    (event: { command: { name: string } }) => {
      emitOlarkEvent('operatorCommand', event)
    },
  )
  window.olark?.('api.box.onShrink', () => {
    emitOlarkEvent('boxShrink')
  })
  window.olark?.('api.box.onExpand', () => {
    emitOlarkEvent('boxExpand')
  })
  window.olark?.('api.chat.onMessageToOperator', () => {
    emitOlarkEvent('messageToOperator')
  })
  window.olark?.('api.chat.onMessageToVisitor', () => {
    emitOlarkEvent('messageToVisitor')
  })

  eventHandlersRegistered = true
}

const startOlarkLoad = (mode: OlarkEmbedMode): Promise<void> =>
  withTimeout(
    new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.id = OLARK_LOADER_SCRIPT_ID
      script.async = true
      script.src = OLARK_LOADER_SRC

      script.onerror = () => {
        script.remove()
        reject(new Error('Failed to load Olark'))
      }

      createOlarkStub()
      applyOlarkConfig(mode)

      window.olark?.('identify', olarkId)
      window.olark?.('api.chat.onReady', () => {
        registerOlarkEventHandlers()
        olarkReady = true
        resolve()
      })

      document.body.appendChild(script)
    }),
    OLARK_READY_TIMEOUT_MS,
  )

export const resetOlarkLoadState = (): void => {
  olarkReady = false
  pendingLoad = null
  loadedMode = null
  eventHandlersRegistered = false
  delete window.olark
  document.getElementById(OLARK_LOADER_SCRIPT_ID)?.remove()
}

export const loadOlarkScript = async (
  options?: LoadOlarkOptions,
): Promise<void> => {
  if (olarkReady) {
    options?.onReady?.()
    return
  }

  if (!pendingLoad) {
    if (document.getElementById(OLARK_LOADER_SCRIPT_ID)) {
      resetOlarkLoadState()
    }

    const mode = options?.mode ?? loadedMode ?? 'floating'
    loadedMode = mode
    pendingLoad = startOlarkLoad(mode).catch(error => {
      pendingLoad = null
      loadedMode = null
      throw error
    })
  }

  try {
    await pendingLoad
    options?.onReady?.()
  } catch (error) {
    resetOlarkLoadState()
    throw error
  }
}

export const expandOlarkBox = (): void => {
  window.olark?.('api.box.expand')
}

export const hideOlarkBox = (): void => {
  window.olark?.('api.box.hide', true)
  window.olark?.('api.box.shrink')
}

export const showOlarkBox = (): void => {
  window.olark?.('api.box.show')
  expandOlarkBox()
}
